"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { PillarView } from "@/lib/catalog-db";
import { IconCaret, IconMenu, IconClose, IconArrowRight } from "@/lib/icons";
import { SiteSearch } from "@/components/site/SiteSearch";
import { ThemeToggle } from "@/components/site/ThemeToggle";

/** External links (client platforms / ticket portals) open in a new tab. */
const isExternal = (href: string) => /^https?:\/\//i.test(href);

/**
 * Sticky site header.
 * Desktop (≥1100px): inline nav + four-column "Serviços" mega menu (JS-controlled
 * with a close delay so the cursor can travel into the panel without it collapsing)
 * + an "Acesso" dropdown linking to the client platforms / ticket portals.
 * Mobile (<1100px): a compact bar (logo + search + burger) — the "Acesso" dropdown
 * and the urgency bar are hidden by CSS — plus a modern off-canvas drawer whose
 * footer carries the same "Acesso & Suporte" links.
 */
export function SiteHeader({
  pillars,
  logo,
  logoLight,
  nav,
  support,
}: {
  pillars: PillarView[];
  logo: string;
  logoLight: string;
  nav: { label: string; href: string; megaMenu: boolean }[];
  support: { label: string; href: string }[];
}) {
  const pathname = usePathname();
  const [megaOpen, setMegaOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const accessRef = useRef<HTMLDivElement>(null);

  // Close menu/drawer/dropdown on route change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset on navigation
    setMegaOpen(false);
    setDrawerOpen(false);
    setAccessOpen(false);
  }, [pathname]);

  // While the "Acesso" dropdown is open: close on outside click or Escape.
  useEffect(() => {
    if (!accessOpen) return;
    const onDown = (e: MouseEvent) => {
      if (accessRef.current && !accessRef.current.contains(e.target as Node)) setAccessOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAccessOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [accessOpen]);

  // While the drawer is open: lock body scroll, ESC to close, move focus into the
  // drawer, trap Tab inside it, and restore focus to the burger on close.
  useEffect(() => {
    if (!drawerOpen) return;
    const burger = burgerRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerCloseRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const root = drawerRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      burger?.focus();
    };
  }, [drawerOpen]);

  function openMega() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  }
  function scheduleCloseMega() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), 350);
  }
  const isActive = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <header className="site-head">
        <div className="site-head__inner">
          <Link href="/" className="brand" aria-label="Global ERP — página inicial">
            {/* eslint-disable-next-line @next/next/no-img-element -- editable logo, any host */}
            <img className="brand__logo brand__logo--escuro" src={logo} alt="Global ERP — tecnologia empresarial" />
            {/* eslint-disable-next-line @next/next/no-img-element -- editable logo, any host */}
            <img className="brand__logo brand__logo--claro" src={logoLight} alt="Global ERP — tecnologia empresarial" />
          </Link>

          <nav className="nav" aria-label="Principal">
            {nav.map((item) =>
              item.megaMenu ? (
                <span
                  key={item.href}
                  className="has-menu"
                  onMouseEnter={openMega}
                  onMouseLeave={scheduleCloseMega}
                  onFocus={openMega}
                  onBlur={scheduleCloseMega}
                >
                  <Link
                    href={item.href}
                    className={isActive(item.href) ? "active" : undefined}
                    aria-current={isActive(item.href) ? "page" : undefined}
                    aria-expanded={megaOpen}
                  >
                    {item.label}
                    <IconCaret className="nav__caret" />
                  </Link>
                  <div
                    className="mega"
                    style={megaOpen ? { display: "grid" } : undefined}
                    onMouseEnter={openMega}
                    onMouseLeave={scheduleCloseMega}
                  >
                    {pillars.map((p) => (
                      <div className="mega-col" key={p.accent}>
                        <h5>
                          <span className="swatch" style={{ background: p.swatch }} />
                          {p.shortName}
                        </h5>
                        <ul>
                          {p.services.map((s) => (
                            <li key={s.slug}>
                              <Link href={`/servicos/${s.slug}`}>
                                {s.label}
                                {s.priority ? " ⚡" : ""}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </span>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className={isActive(item.href) ? "active" : undefined}
                  aria-current={isActive(item.href) ? "page" : undefined}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="head-actions">
            <ThemeToggle />
            <SiteSearch />
            {support.length > 0 && (
              <div className="head-access" ref={accessRef}>
                <button
                  type="button"
                  className="btn btn--primary head-access__btn"
                  aria-haspopup="true"
                  aria-expanded={accessOpen}
                  onClick={() => setAccessOpen((v) => !v)}
                >
                  Acesso <IconCaret className="head-access__caret" />
                </button>
                {accessOpen && (
                  <div className="head-access__menu" role="menu">
                    {support.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="head-access__item"
                        target={isExternal(item.href) ? "_blank" : undefined}
                        rel={isExternal(item.href) ? "noopener noreferrer" : undefined}
                        onClick={() => setAccessOpen(false)}
                      >
                        <span>{item.label}</span>
                        <IconArrowRight width={15} height={15} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button
              ref={burgerRef}
              type="button"
              className="nav-burger"
              aria-label="Abrir menu"
              aria-expanded={drawerOpen}
              aria-controls="nav-drawer"
              onClick={() => setDrawerOpen(true)}
            >
              <IconMenu width={22} height={22} />
            </button>
          </div>
        </div>
      </header>

      {/* ---- Off-canvas mobile drawer ---- */}
      <div
        className={"nav-drawer-backdrop" + (drawerOpen ? " is-open" : "")}
        onClick={closeDrawer}
        aria-hidden
      />
      <aside
        ref={drawerRef}
        id="nav-drawer"
        className={"nav-drawer" + (drawerOpen ? " is-open" : "")}
        aria-label="Menu"
        inert={!drawerOpen}
      >
        <div className="nav-drawer__head">
          <Link href="/" className="brand" onClick={closeDrawer} aria-label="Início">
            {/* eslint-disable-next-line @next/next/no-img-element -- editable logo, any host */}
            <img className="brand__logo brand__logo--escuro" src={logo} alt="Global ERP" />
            {/* eslint-disable-next-line @next/next/no-img-element -- editable logo, any host */}
            <img className="brand__logo brand__logo--claro" src={logoLight} alt="Global ERP" />
          </Link>
          <button ref={drawerCloseRef} type="button" className="nav-drawer__close" aria-label="Fechar menu" onClick={closeDrawer}>
            <IconClose width={20} height={20} />
          </button>
        </div>

        <nav className="nav-drawer__nav" aria-label="Navegação móvel">
          {nav.map((item) => (
            <Link key={item.href} href={item.href} className="nav-drawer__link" onClick={closeDrawer}>
              <span>{item.label}</span>
              <IconArrowRight width={16} height={16} />
            </Link>
          ))}
        </nav>

        <div className="nav-drawer__group">
          <p className="nav-drawer__group-title">Serviços por pilar</p>
          {pillars.map((p) => (
            <div className="nav-drawer__pillar" key={p.accent}>
              <h5>
                <span className="swatch" style={{ background: p.swatch }} />
                {p.shortName}
              </h5>
              <ul>
                {p.services.map((s) => (
                  <li key={s.slug}>
                    <Link href={`/servicos/${s.slug}`} onClick={closeDrawer}>
                      {s.label}
                      {s.priority ? " ⚡" : ""}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <Link href="/servicos" className="nav-drawer__all" onClick={closeDrawer}>
            Ver todos os serviços <IconArrowRight width={14} height={14} />
          </Link>
        </div>

        {support.length > 0 && (
          <div className="nav-drawer__cta">
            <p className="nav-drawer__group-title">Acesso &amp; Suporte</p>
            {support.map((item, i) => (
              <a
                key={item.href}
                href={item.href}
                className={"btn " + (i === 0 ? "btn--primary" : "btn--ghost")}
                target={isExternal(item.href) ? "_blank" : undefined}
                rel={isExternal(item.href) ? "noopener noreferrer" : undefined}
                onClick={closeDrawer}
              >
                {item.label}
              </a>
            ))}
          </div>
        )}
      </aside>
    </>
  );
}
